import { AbsoluteCenter, Box, HStack, Link, VStack } from '@web/components/atoms/Layout'
import { ProfileData } from '@web/graphql-types'
import LeaderboardProfile from './LeaderboardProfile'
import { useEffect, useRef, useState } from 'react'
import { IconButton } from '@web/components/atoms/Button'
import { LuChevronLeft, LuChevronRight } from '@web/components/atoms/Icons'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { Skeleton } from '@web/components/atoms/Skeleton'
import { SectionBody } from '@web/components/atoms/Typography'
import { PageButton } from '../PageButton'

type LeaderboardGroupProps = {
  data: ProfileData[]
  isLoading?: boolean
  registerUrl?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styles: Record<string, any>
}

const LeaderboardGroup = ({ data, isLoading, registerUrl, styles }: LeaderboardGroupProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const arrowColor = useColorWithOpacity(styles?.arrowColor ?? '#000000', '1')
  const [profileLimit, setProfileLimit] = useState(data.length)

  useEffect(() => {
    if (isLoading) {
      return
    }

    setProfileLimit(styles?.isShowAll ? styles?.limit || data.length : data.length)
  }, [data, isLoading, styles?.limit])

  return (
    <Box
      display="flex"
      w="100%"
      css={{
        '& .c-leaderboard__scroll-button': {
          alignSelf: 'stretch',
          height: 'auto',
          display: styles?.isShowAll || data.length === 0 ? 'none' : 'flex',
          px: '12px',
          flexDirection: 'column',
          justifyContent: 'center',
          cursor: 'pointer',
          '& svg': {
            color: arrowColor,
          },
        },
      }}
    >
      <Box
        className="c-leaderboard__scroll-button"
        left="0"
        onClick={() => {
          ref.current?.scrollTo({
            left: Math.max((ref.current?.scrollLeft ?? 0) - (ref.current?.clientWidth ?? 0), 0),
            behavior: 'smooth',
          })
        }}
      >
        <IconButton buttonAppearance="text">
          <LuChevronLeft />
        </IconButton>
      </Box>
      <Box
        maxW="100%"
        width="100%"
        overflowX={styles?.isShowAll ? 'auto' : 'scroll'}
        scrollbarWidth="none"
        ref={ref}
        css={{
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            width: '0px',
            height: '0px',
          },
        }}
        position="relative"
      >
        <HStack
          gap="24px"
          w="fit-content"
          css={styles?.isShowAll ? { flexWrap: 'wrap', justifyContent: 'space-evenly' } : {}}
        >
          {isLoading && Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} w="100%" h="72px" />)}
          {!isLoading && data.length === 0 && (
            <Box h="72px">
              <AbsoluteCenter
                textAlign="center"
                backgroundColor="rgba(255, 255, 255, .8)"
                padding="12px 24px"
                borderRadius="4px"
                w={`min(400px, 90%)`}
                h="72px"
                transition="width 0.2s ease-in-out, height 0.2s ease-in-out"
              >
                <AbsoluteCenter w="100%">
                  <SectionBody fontSize="16px">
                    No fundraisers - Be the first to <Link href={registerUrl}>register</Link>
                  </SectionBody>
                </AbsoluteCenter>
              </AbsoluteCenter>
            </Box>
          )}
          {data.slice(0, profileLimit).map((profile, index) => (
            <LeaderboardProfile key={index} data={profile} index={index} styles={styles.profile} />
          ))}
        </HStack>
        {styles?.isShowAll && data.length > profileLimit && (
          <VStack w="100%" mt="16px">
            <PageButton
              id="load-more-profiles"
              name="Secondary"
              onClick={() => {
                setProfileLimit(profileLimit + 10)
              }}
              size="xs"
              buttonText="Load more"
            />
          </VStack>
        )}
      </Box>
      <Box
        className="c-leaderboard__scroll-button"
        right="0"
        onClick={() => {
          ref.current?.scrollTo({
            left: (ref.current?.scrollLeft ?? 0) + (ref.current?.clientWidth ?? 0),
            behavior: 'smooth',
          })
        }}
      >
        <IconButton buttonAppearance="text">
          <LuChevronRight />
        </IconButton>
      </Box>
    </Box>
  )
}

export default LeaderboardGroup
